import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/pathways";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Notes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [feed, setFeed] = useState(null);
  const [form, setForm] = useState({ title: "", kind: "want", wait_until: "", target_price: "" });

  const load = () => {
    api.get("/saved").then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/feed").then((r) => setFeed(r.data)).catch(() => setFeed(null));
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (user === null) return <div className="max-w-7xl mx-auto px-6 py-20 font-display text-2xl text-stone-500">…</div>;
  if (!user) return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
      <div className="eyebrow mb-4">NOTES</div>
      <h1 className="font-display text-4xl md:text-6xl mb-6">SAVE THE THINGS<br />YOU'RE WAITING TO BUY.</h1>
      <p className="font-serif text-stone-400 mb-8">Log in to keep your wants, targets and saved audits in one place.</p>
      <Link to="/login" data-testid="notes-login-cta" className="font-label text-xs tracking-widest bg-[#EF3340] text-white px-8 py-4 inline-block">LOG IN / SIGN UP →</Link>
    </div>
  );

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Give it a name."); return; }
    try {
      await api.post("/saved", { title: form.title, kind: form.kind, wait_until: form.wait_until,
        target_price: form.target_price ? parseFloat(form.target_price) : null });
      setForm({ title: "", kind: "want", wait_until: "", target_price: "" });
      load(); toast.success("Saved.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/saved/${id}`); load(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
      <div className="eyebrow mb-4">NOTES</div>
      <h1 className="font-display text-4xl md:text-6xl leading-[0.95] mb-10">YOUR MARKED-UP LIST.</h1>

      <form onSubmit={add} className="border hairline bg-[#141217] p-6 mb-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
        <label className="lg:col-span-2 block">
          <span className="font-label text-[10px] tracking-widest text-stone-500 block mb-2">ITEM</span>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="MacBook Pro" data-testid="note-title"
            className="w-full bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#EF3340]" />
        </label>
        <label className="block">
          <span className="font-label text-[10px] tracking-widest text-stone-500 block mb-2">WAIT UNTIL</span>
          <input value={form.wait_until} onChange={(e) => setForm({ ...form, wait_until: e.target.value })} placeholder="November" data-testid="note-wait"
            className="w-full bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#EF3340]" />
        </label>
        <label className="block">
          <span className="font-label text-[10px] tracking-widest text-stone-500 block mb-2">TARGET $</span>
          <input value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} type="number" placeholder="1400" data-testid="note-target"
            className="w-full bg-transparent border hairline px-4 py-3 font-label text-sm outline-none focus:border-[#EF3340]" />
        </label>
        <button type="submit" data-testid="note-add-btn" className="font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-3 flex items-center justify-center gap-2 hover:bg-[#cc2a35] transition-colors">
          <Plus className="w-4 h-4" /> ADD
        </button>
      </form>

      {items.length > 0 && (
        <div className="mb-14">
          <h2 className="font-label text-sm tracking-widest text-[#EF3340] mb-4">SAVED</h2>
          <div className="border-t hairline">
            {items.map((it) => (
              <div key={it.id} data-testid={`saved-${it.id}`} className="border-b hairline py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="font-label text-[10px] tracking-widest text-[#D4AF37] sm:w-24">{it.kind?.toUpperCase()}</span>
                <span className="font-display text-xl flex-1">{it.title}</span>
                {it.wait_until && <span className="font-label text-xs text-stone-400">WAIT: {it.wait_until}</span>}
                {it.target_price != null && <span className="font-label text-xs text-[#5FB878]">TARGET {money(it.target_price)}</span>}
                <button onClick={() => del(it.id)} data-testid={`saved-del-${it.id}`} className="text-stone-600 hover:text-[#EF3340] p-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-label text-sm tracking-widest text-[#EF3340]">YOUR OPPORTUNITY FEED</h2>
          {!user.is_pro && <Link to="/pro" className="font-label text-[10px] tracking-widest text-[#D4AF37]">GO PRO FOR MORE →</Link>}
        </div>
        {feed?.categories?.length > 0 && (
          <p className="font-serif text-sm text-stone-500 mb-5">Based on what you've been auditing: {feed.categories.join(" · ")}</p>
        )}
        {!user.is_pro && (
          <div className="border hairline bg-[#141217] p-6 mb-6">
            <p className="font-serif text-lg text-stone-200">You've got more to audit. The Cheaper Way Pro unlocks unlimited audits, saved results, a personalized feed, and Pro-only finds.</p>
            <Link to="/pro" data-testid="feed-upgrade" className="mt-4 inline-block font-label text-xs tracking-widest bg-[#EF3340] text-white px-6 py-3">GO PRO →</Link>
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(feed?.early_access || []).map((o) => (
            <a key={o.id} href={o.affiliate_url || o.destination_url || o.official_url} target="_blank" rel="noopener noreferrer" data-testid={`feed-early-${o.id}`}
              className="group border-2 border-[#D4AF37] bg-[#141217] p-6 hover:border-[#EF3340] transition-colors">
              <span className="font-label text-[10px] tracking-widest text-[#D4AF37] border border-[#D4AF37] px-2 py-1">PRO · EARLY ACCESS</span>
              <h3 className="font-display text-xl mt-3 mb-2">{o.title}</h3>
              <p className="font-serif text-sm text-stone-400">{o.description}</p>
            </a>
          ))}
          {(feed?.opportunities || []).map((o) => (
            <a key={o.id} href={o.affiliate_url || o.destination_url || o.official_url} target="_blank" rel="noopener noreferrer" data-testid={`feed-opp-${o.id}`}
              className="group border hairline bg-[#141217] p-6 hover:border-[#EF3340] transition-colors">
              <span className="font-label text-[10px] tracking-widest text-[#EF3340] border border-[#EF3340]/40 px-2 py-1">{o.type}</span>
              <h3 className="font-display text-xl mt-3 mb-2 group-hover:text-[#EF3340] transition-colors">{o.title}</h3>
              <p className="font-serif text-sm text-stone-400">{o.description}</p>
            </a>
          ))}
        </div>
        {(!feed || (feed.opportunities?.length === 0 && (feed.early_access || []).length === 0)) && (
          <p className="font-serif text-stone-500">Run a few audits and your feed will fill in with relevant finds.</p>
        )}
      </div>
    </div>
  );
}
